/**
 * useMusicStore - 新しい音楽画面（/music 配下）専用のプレイヤーストア
 *
 * 実際の音声再生は `@/lib/music-audio-engine` がこのストアを購読して行う。
 * UI はこのストアのアクションだけを呼び、audio 要素を直接触らない。
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { useMusicPlayerStore } from '@/store/useMusicPlayerStore'

export type MusicRepeatMode = 'off' | 'all' | 'one'

export type MusicTrack = {
  episodeId: number
  productId: number
  title: string
  artist: string | null
  /** アーティストのタグID（アーティスト画面への遷移に使う） */
  artistTagId: number | null
  album: string | null
  thumbnail: string | null
  /** 再生時間（秒） */
  duration: number
}

type MusicState = {
  queue: MusicTrack[]
  currentIndex: number
  isPlaying: boolean
  /** 再生位置（秒） */
  currentTime: number
  /** 実ファイルから取得した再生時間（秒）。未取得なら 0 */
  duration: number
  /**
   * シーク要求の世代番号。
   * currentTime は audio の timeupdate でも更新されるため、
   * 「ユーザー操作によるシーク」をエンジン側が区別するために使う。
   */
  seekVersion: number
  shuffle: boolean
  repeatMode: MusicRepeatMode
  /** フルスクリーンプレイヤー（BottomSheet）の開閉 */
  isPlayerOpen: boolean
  favoriteEpisodeIds: number[]
  /** ステム（ボーカル／インスト分離）再生の ON/OFF */
  stemEnabled: boolean
  /** ボーカル音量 (0-1) */
  vocalVolume: number
  /** インスト音量 (0-1) */
  instVolume: number
  /** 再生中の曲に分離済みファイルがあるか。エンジンが判定して書き込む */
  stemsAvailable: boolean
  /** 再生方式の切替中。トグルを二重に押させないために使う */
  isStemSwitching: boolean
}

type MusicActions = {
  playQueue: (tracks: MusicTrack[], startIndex: number, options?: { shuffle?: boolean }) => void
  playAt: (index: number) => void
  playNext: () => void
  playPrev: () => void
  togglePlay: () => void
  setPlaying: (playing: boolean) => void
  seekTo: (seconds: number) => void
  setCurrentTime: (seconds: number) => void
  setDuration: (seconds: number) => void
  setPlayerOpen: (open: boolean) => void
  toggleShuffle: () => void
  cycleRepeatMode: () => void
  toggleFavorite: (episodeId: number) => void
  /** 再生中の曲の直後に差し込む */
  playTrackNext: (track: MusicTrack) => void
  /** キューの最後に追加する */
  addTrackToQueue: (track: MusicTrack) => void
  reorderQueue: (fromIndex: number, toIndex: number) => void
  removeFromQueue: (index: number) => void
  clearUpcoming: () => void
  setStemEnabled: (enabled: boolean) => void
  setVocalVolume: (volume: number) => void
  setInstVolume: (volume: number) => void
  setStemsAvailable: (available: boolean) => void
  setStemSwitching: (switching: boolean) => void
}

const REPEAT_CYCLE: MusicRepeatMode[] = ['off', 'all', 'one']

/** 直前まで再生していた曲へ戻すか、頭出しにするかの境界（秒） */
const RESTART_THRESHOLD = 3

const clamp01 = (value: number): number => {
  if (!Number.isFinite(value)) return 1
  return Math.min(Math.max(value, 0), 1)
}

/** Fisher-Yates シャッフル（元配列は変更しない） */
const shuffleTracks = (tracks: MusicTrack[]): MusicTrack[] => {
  const result = [...tracks]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const tmp = result[i]
    result[i] = result[j]
    result[j] = tmp
  }
  return result
}

/** 別のトラックを再生し始めるときに共通で初期化する状態 */
const START_TRACK_PATCH = { currentTime: 0, duration: 0, isPlaying: true } as const

/** 現在のトラックを頭出しする（seekVersion を進めてエンジンにシーク要求を伝える） */
const restartPatch = (state: MusicState) => ({ currentTime: 0, seekVersion: state.seekVersion + 1 })

/** 再生履歴は既存の useMusicPlayerStore.playHistory に集約して記録する */
const recordHistory = (track: MusicTrack | undefined) => {
  if (!track) return
  useMusicPlayerStore.getState().recordPlayHistory({
    episodeId: track.episodeId,
    title: track.title,
    thumbnailPath: track.thumbnail,
    artist: track.artist ?? undefined,
    album: track.album ?? undefined,
    productId: track.productId,
  })
}

export const useMusicStore = create<MusicState & MusicActions>()(
  persist(
    (set, get) => ({
      queue: [],
      currentIndex: 0,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      seekVersion: 0,
      shuffle: false,
      repeatMode: 'off',
      isPlayerOpen: false,
      favoriteEpisodeIds: [],
      stemEnabled: false,
      vocalVolume: 1,
      instVolume: 1,
      stemsAvailable: false,
      isStemSwitching: false,

      playQueue: (tracks, startIndex, options) => {
        if (tracks.length === 0) return

        if (options?.shuffle) {
          const shuffled = shuffleTracks(tracks)
          set({ queue: shuffled, currentIndex: 0, shuffle: true, ...START_TRACK_PATCH })
          recordHistory(shuffled.at(0))
          return
        }

        const safeIndex = Math.min(Math.max(startIndex, 0), tracks.length - 1)
        // シャッフル再生の直後に通常再生した場合、シャッフル状態を明示的に解除する
        set({ queue: tracks, currentIndex: safeIndex, shuffle: false, ...START_TRACK_PATCH })
        recordHistory(tracks.at(safeIndex))
      },

      playAt: (index) => {
        const { queue } = get()
        if (index < 0 || index >= queue.length) return
        set({ currentIndex: index, ...START_TRACK_PATCH })
        recordHistory(queue.at(index))
      },

      playNext: () => {
        const { queue, currentIndex, repeatMode } = get()
        if (queue.length === 0) return

        if (repeatMode === 'one') {
          set((state) => ({ ...restartPatch(state), isPlaying: true }))
          return
        }

        const nextIndex = currentIndex + 1
        if (nextIndex < queue.length) {
          set({ currentIndex: nextIndex, ...START_TRACK_PATCH })
          recordHistory(queue.at(nextIndex))
          return
        }
        if (repeatMode === 'all') {
          set({ currentIndex: 0, ...START_TRACK_PATCH })
          recordHistory(queue.at(0))
          return
        }
        set((state) => ({ ...restartPatch(state), isPlaying: false }))
      },

      playPrev: () => {
        const { queue, currentIndex, currentTime, repeatMode } = get()
        if (queue.length === 0) return

        // 再生が進んでいる場合はまず頭出し
        if (currentTime > RESTART_THRESHOLD) {
          set(restartPatch)
          return
        }

        if (currentIndex - 1 >= 0) {
          set({ currentIndex: currentIndex - 1, ...START_TRACK_PATCH })
          recordHistory(queue.at(currentIndex - 1))
          return
        }
        if (repeatMode === 'all') {
          set({ currentIndex: queue.length - 1, ...START_TRACK_PATCH })
          recordHistory(queue.at(queue.length - 1))
          return
        }
        set(restartPatch)
      },

      togglePlay: () => {
        if (get().queue.length === 0) return
        set((state) => ({ isPlaying: !state.isPlaying }))
      },

      setPlaying: (playing) => {
        if (playing && get().queue.length === 0) return
        set({ isPlaying: playing })
      },

      seekTo: (seconds) => {
        set((state) => ({ currentTime: Math.max(0, seconds), seekVersion: state.seekVersion + 1 }))
      },

      setCurrentTime: (seconds) => {
        set({ currentTime: seconds })
      },

      setDuration: (seconds) => {
        set({ duration: Number.isFinite(seconds) && seconds > 0 ? seconds : 0 })
      },

      setPlayerOpen: (open) => {
        set({ isPlayerOpen: open })
      },

      toggleShuffle: () => {
        const { shuffle, queue, currentIndex } = get()

        // 解除するときは並びをそのまま残す（聴いている流れを壊さない）
        if (shuffle) {
          set({ shuffle: false })
          return
        }

        // 再生中の曲はそのままに、これから流れる曲だけを並べ替える
        const upcoming = queue.slice(currentIndex + 1)
        if (upcoming.length < 2) {
          set({ shuffle: true })
          return
        }

        set({ shuffle: true, queue: [...queue.slice(0, currentIndex + 1), ...shuffleTracks(upcoming)] })
      },

      cycleRepeatMode: () => {
        const idx = REPEAT_CYCLE.indexOf(get().repeatMode)
        set({ repeatMode: REPEAT_CYCLE[(idx + 1) % REPEAT_CYCLE.length] })
      },

      toggleFavorite: (episodeId) => {
        set((state) => ({
          favoriteEpisodeIds: state.favoriteEpisodeIds.includes(episodeId)
            ? state.favoriteEpisodeIds.filter((id) => id !== episodeId)
            : [...state.favoriteEpisodeIds, episodeId],
        }))
      },

      playTrackNext: (track) => {
        const { queue, currentIndex } = get()
        // キューが空なら差し込み先がないので、そのまま再生を始める
        if (queue.length === 0) {
          get().playQueue([track], 0)
          return
        }
        set({ queue: [...queue.slice(0, currentIndex + 1), track, ...queue.slice(currentIndex + 1)] })
      },

      addTrackToQueue: (track) => {
        const { queue } = get()
        if (queue.length === 0) {
          get().playQueue([track], 0)
          return
        }
        set({ queue: [...queue, track] })
      },

      reorderQueue: (fromIndex, toIndex) => {
        const { queue, currentIndex } = get()
        if (fromIndex === toIndex) return

        const newQueue = [...queue]
        const [moved] = newQueue.splice(fromIndex, 1)
        newQueue.splice(toIndex, 0, moved)

        // 再生中トラックの位置を追跡する
        let newCurrentIndex = currentIndex
        if (fromIndex === currentIndex) {
          newCurrentIndex = toIndex
        } else if (fromIndex < currentIndex && toIndex >= currentIndex) {
          newCurrentIndex = currentIndex - 1
        } else if (fromIndex > currentIndex && toIndex <= currentIndex) {
          newCurrentIndex = currentIndex + 1
        }
        set({ queue: newQueue, currentIndex: newCurrentIndex })
      },

      removeFromQueue: (index) => {
        const { queue, currentIndex } = get()
        if (index < 0 || index >= queue.length || index === currentIndex) return
        set({
          queue: queue.filter((_, i) => i !== index),
          currentIndex: index < currentIndex ? currentIndex - 1 : currentIndex,
        })
      },

      clearUpcoming: () => {
        const { queue, currentIndex } = get()
        set({ queue: queue.slice(0, currentIndex + 1) })
      },

      setStemEnabled: (enabled) => {
        set({ stemEnabled: enabled })
      },

      setVocalVolume: (volume) => {
        set({ vocalVolume: clamp01(volume) })
      },

      setInstVolume: (volume) => {
        set({ instVolume: clamp01(volume) })
      },

      setStemsAvailable: (available) => {
        set({ stemsAvailable: available })
      },

      setStemSwitching: (switching) => {
        set({ isStemSwitching: switching })
      },
    }),
    {
      name: 'music',
      partialize: (state) => ({
        queue: state.queue,
        currentIndex: state.currentIndex,
        shuffle: state.shuffle,
        repeatMode: state.repeatMode,
        favoriteEpisodeIds: state.favoriteEpisodeIds,
        stemEnabled: state.stemEnabled,
        vocalVolume: state.vocalVolume,
        instVolume: state.instVolume,
      }),
    },
  ),
)
